import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { useManagedOrg } from '@/hooks/useManagedOrg'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Avatar } from '@/components/Avatar'
import { RoleBadge } from '@/components/RoleBadge'
import type { LearnerSummaryRow, LearnerStatus } from '@/domain/analytics'

const STATUS_LABEL: Record<LearnerStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  needs_attention: 'Needs support',
  completed_all: 'Completed',
}

const STATUS_STYLE: Record<LearnerStatus, string> = {
  not_started: 'bg-white/10 text-white/60 ring-1 ring-white/15',
  in_progress: 'bg-teal-500/15 text-teal-300 ring-1 ring-teal-500/30',
  needs_attention: 'bg-coral-500/15 text-coral-500 ring-1 ring-coral-500/30',
  completed_all: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
}

// P2 development-mode learner management (org_admin/super_admin see the
// full organization roster; teacher sees only school_student rows in their
// own org — enforced via useManagedOrg() + an explicit role filter, not a
// full production user-management backend, per the P2 brief).
export function LearnerManagementPage() {
  const { currentUser } = useAuth()
  const { organizations, orgAnalytics } = useServices()
  const { orgId, isSuperAdmin, loadingOrgs } = useManagedOrg()
  const isTeacher = currentUser?.role === 'teacher'

  const [orgName, setOrgName] = useState('')
  const [rows, setRows] = useState<LearnerSummaryRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    const roleFilter = isTeacher ? (['school_student'] as const) : undefined
    Promise.all([
      organizations.getById(orgId),
      orgAnalytics.listLearnerRows(orgId, roleFilter ? [...roleFilter] : undefined),
    ]).then(([org, learnerRows]) => {
      setOrgName(org?.name ?? orgId)
      setRows([...learnerRows].sort((a, b) => a.displayName.localeCompare(b.displayName)))
      setLoading(false)
    })
  }, [orgId, organizations, orgAnalytics, isTeacher])

  if (!currentUser) return null
  if (isSuperAdmin && loadingOrgs) return <LoadingSpinner label="Loading organizations…" />
  if (!orgId) return null
  if (loading) return <LoadingSpinner label="Loading learners…" />

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/team" className="text-sm text-white/50 hover:text-white">
        ← Back to {isTeacher ? 'Class Overview' : 'Organization Dashboard'}
      </Link>
      <h1 className="mt-3 font-display text-3xl font-bold text-white">
        {orgName} — {isTeacher ? 'Students' : 'Learners'}
      </h1>
      <p className="mt-2 text-sm text-white/60">{rows.length} {rows.length === 1 ? 'learner' : 'learners'} in view.</p>

      <div className="mt-6 cyber-panel overflow-x-auto p-0">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-white/40">
              <th className="px-4 py-3 font-medium">Learner</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Episodes</th>
              <th className="px-4 py-3 font-medium">Quizzes</th>
              <th className="px-4 py-3 font-medium">Avg score</th>
              <th className="px-4 py-3 font-medium">Cyber Points</th>
              <th className="px-4 py-3 font-medium">Badges</th>
              <th className="px-4 py-3 font-medium">Certificate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.userId} className="border-b border-white/5 last:border-0">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={r.displayName} color={r.avatarColor} size={32} />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-white">{r.displayName}</p>
                      <RoleBadge role={r.role} />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`cyber-pill ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </td>
                <td className="px-4 py-3 text-white/80">
                  {r.episodesCompleted}/{r.episodesTotal}
                </td>
                <td className="px-4 py-3 text-white/80">
                  {r.quizzesPassed}/{r.quizzesAttempted || 0} passed
                </td>
                <td className="px-4 py-3 text-white/80">{r.averageScorePercent !== null ? `${r.averageScorePercent}%` : '—'}</td>
                <td className="px-4 py-3 font-semibold text-teal-300">{r.xpTotal}</td>
                <td className="px-4 py-3 text-white/80">{r.badgesEarned}</td>
                <td className="px-4 py-3 text-white/80">{r.certificatesIssued > 0 ? 'Issued' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-white/50">
            No {isTeacher ? 'students' : 'learners'} found in this organization yet.
          </p>
        )}
      </div>
    </div>
  )
}
